import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import useAuthStore from '../../store/authStore';
import { fetchParentStudents } from '../../api/students';
import { SUBJECT_OPTIONS } from '../../components/forms/StudentFormDialog';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };
const avatarColors = ['#4357AD', '#22C55E', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4'];
const translateSubject = (val) => SUBJECT_OPTIONS.find((o) => o.value === val)?.label || val || '';

const ParentTutorsPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['parent-students', user?.id],
    queryFn: () => fetchParentStudents(user.id),
    enabled: Boolean(user?.id),
  });

  const tutors = [];
  const seen = new Set();
  (data || []).forEach((s) => {
    if (s.tutorId && !seen.has(s.tutorId)) {
      seen.add(s.tutorId);
      tutors.push({
        id: s.tutorId,
        name: s.tutorName,
        students: (data || []).filter((st) => st.tutorId === s.tutorId),
      });
    }
  });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: B.primary }} /></Box>;
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h4" fontWeight={800} sx={{ color: B.dark }}>Наші репетитори</Typography>
        <Typography variant="body1" sx={{ color: '#8891A5', mt: 0.5 }}>
          Оберіть репетитора для перегляду профілю
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        {tutors.map((tutor, i) => {
          const color = avatarColors[i % avatarColors.length];
          const initials = tutor.name
            ?.split(' ')
            .map((w) => w.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2) || '?';
          return (
            <Card
              key={tutor.id}
              sx={{
                ...cardSx,
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': { boxShadow: '0 4px 20px rgba(67,87,173,0.12)', transform: 'translateY(-2px)' },
              }}
            >
              <CardActionArea onClick={() => navigate(`/parent/tutor/${tutor.id}`)} sx={{ borderRadius: 4 }}>
                <CardContent sx={{ py: 2.5, px: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: `${color}18`,
                        color,
                        fontWeight: 700,
                        fontSize: 20,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark }} noWrap>
                        {tutor.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#8891A5' }}>
                        {tutor.students.map((s) => s.name).join(', ')}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ color: '#C4B5FD', fontSize: 20, flexShrink: 0 }} />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Stack>
  );
};

export default ParentTutorsPage;
